/*
 * googleMapsLoader
 * ----------------
 * Lazy loads the Google Maps JS API with the Places library exactly once
 * per page. All callers share the same in-flight promise so we never inject
 * the script tag twice.
 *
 * Reads the key from `REACT_APP_GOOGLE_MAPS_API_KEY` at build time (CRA
 * inlines REACT_APP_* env vars). If no key is configured the loader
 * resolves to `null` and consumers should fall back to the stub list.
 */

let cached = null;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const isGoogleMapsConfigured = () => !!GOOGLE_MAPS_API_KEY;

export const loadGoogleMaps = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);

  // Already loaded on this page (either previously by us or by another part
  // of the app).
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google);
  }

  if (cached) return cached;

  if (!GOOGLE_MAPS_API_KEY) {
    cached = Promise.resolve(null);
    return cached;
  }

  cached = new Promise((resolve, reject) => {
    // Use a global callback name so the script can ping us when ready.
    const callbackName = '__loanGatewayGoogleMapsReady';
    window[callbackName] = () => {
      try {
        delete window[callbackName];
      } catch (e) {
        window[callbackName] = undefined;
      }
      resolve(window.google);
    };

    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        GOOGLE_MAPS_API_KEY
      )}&libraries=places&callback=${callbackName}&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onerror = (err) => {
      cached = null;
      reject(err);
    };
    document.head.appendChild(script);
  });

  return cached;
};
